import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class Form1118SchdKService {

  constructor(private http: HttpClient) { }

  /**
   * Get Scenario Dropdown
   * Action Code: '7q0wiy'
   */
  getScenarioDropDown(
    baseUrl: string,
    taxYear: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    return this._makeLoadRequest(baseUrl, {
      action_code: '7q0wiy',
      tax_year: taxYear,
      scenario: filterParams?.scenario || globalService?.scenario || globalService?.globalParams?.scenario || '',
      jcd_key: filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || '',
      screen_key: filterParams?.screen_key || globalService?.screen_key || globalService?.globalParams?.screen_key || '',
      jcd_ta_key: filterParams?.jcd_ta_key || globalService?.jcd_ta_key || globalService?.globalParams?.jcd_ta_key || '',
      activity_key: filterParams?.activity_key || globalService?.activity_key || globalService?.globalParams?.activity_key || ''
    });
  }

  /**
   * Get Filing Group Dropdown
   * Action Code: '0udsuc'
   */
  getFilingGroupDropdown(
    baseUrl: string,
    taxYear: string,
    scenario: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    return this._makeLoadRequest(baseUrl, {
      action_code: '0udsuc',
      tax_year: taxYear,
      scenario: scenario,
      jcd_key: filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || '',
      screen_key: filterParams?.screen_key || globalService?.screen_key || globalService?.globalParams?.screen_key || '',
      jcd_ta_key: filterParams?.jcd_ta_key || globalService?.jcd_ta_key || globalService?.globalParams?.jcd_ta_key || '',
      activity_key: filterParams?.activity_key || globalService?.activity_key || globalService?.globalParams?.activity_key || ''
    });
  }

  /**
   * Get Filer Entity Dropdown
   * Action Code: '35gc5l'
   */
  getFilerEntityDropdown(
    baseUrl: string,
    taxYear: string,
    scenario: string,
    filingGroupKey: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    return this._makeLoadRequest(baseUrl, {
      action_code: '35gc5l',
      tax_year: taxYear,
      scenario: scenario,
      filing_group_key: filingGroupKey,
      jcd_key: filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || '',
      screen_key: filterParams?.screen_key || globalService?.screen_key || globalService?.globalParams?.screen_key || '',
      jcd_ta_key: filterParams?.jcd_ta_key || globalService?.jcd_ta_key || globalService?.globalParams?.jcd_ta_key || '',
      activity_key: filterParams?.activity_key || globalService?.activity_key || globalService?.globalParams?.activity_key || ''
    });
  }

  /**
   * Get Form Column Descriptions for Schedule K
   * Action Code: 'gwo2xj'
   */
  getFormColumnDescriptions(
    baseUrl: string,
    taxYear: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    return this._makeLoadRequest(baseUrl, {
      action_code: 'gwo2xj',
      tax_year: taxYear,
      scenario: filterParams?.scenario || globalService?.scenario || globalService?.globalParams?.scenario || '',
      jcd_key: filterParams?.jcd_key || globalService?.jcd_key || globalService?.globalParams?.jcd_key || '',
      screen_key: filterParams?.screen_key || globalService?.screen_key || globalService?.globalParams?.screen_key || '',
      jcd_ta_key: filterParams?.jcd_ta_key || globalService?.jcd_ta_key || globalService?.globalParams?.jcd_ta_key || '',
      activity_key: filterParams?.activity_key || globalService?.activity_key || globalService?.globalParams?.activity_key || ''
    });
  }

  /**
   * Get Form Lines and Descriptions for Schedule K
   * Action Code: 'gz06qo'
   */
  getFormLinesAndDescriptions(
    baseUrl: string,
    basket: string,
    jcdKey: string,
    jcdTaKey: string,
    taxYear: string,
    scenario: string,
    filerEntityKey: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    return this._makeLoadRequest(baseUrl, {
      action_code: 'gz06qo',
      basket: basket,
      jcd_key: jcdKey,
      jcd_ta_key: jcdTaKey,
      tax_year: taxYear,
      scenario: scenario,
      filer_entity_key: filerEntityKey,
      screen_key: filterParams?.screen_key || globalService?.screen_key || globalService?.globalParams?.screen_key || '',
      activity_key: filterParams?.activity_key || globalService?.activity_key || globalService?.globalParams?.activity_key || ''
    });
  }

  /**
   * Save Schedule K Basket Amounts
   * Action Code: 'rgq77v'
   *
   * Builds FormData directly (matching AngularJS Form1118SchdKFactory.saveBasketAmounts):
   *   1. Start with GlobalService.globalParams
   *   2. Merge isIssueIDEnabled(tax_year, scenario)
   *   3. Add clob_data (JSON stringified Details) and clob_settings (JSON stringified _data)
   *   4. Override jcd_key, tax_year, scenario, filer_entity_key
   *   5. Build checkLockedbj from filer_combination_key
   *   6. POST as FormData
   *
   * NOTE: Does NOT use JsonObjectFactoryService.saveJSON() because that method
   * requires a live workspaceFactory reference (with activeScreen, filters, data)
   * which is unavailable in the custom element context.
   */
  saveBasketAmounts(
    baseUrl: string,
    returnClobSettingsObj: any,
    dataToSave: any[],
    jcdKey: string,
    taxYear: string,
    scenario: string,
    filerEntityKey: string,
    filerCombinationKey: string,
    globalService: any,
    filterParams: any = {}
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=rgq77v`;

    const jsonObj = JSON.stringify(dataToSave);
    const jsonSettings = JSON.stringify(returnClobSettingsObj);

    // 1. Start with globalParams (matching: params = _.merge({}, GlobalService.globalParams, params))
    const params: any = {};
    if (globalService?.globalParams) {
      Object.assign(params, globalService.globalParams);
    }

    // 2. Merge isIssueIDEnabled (matching: params = _.merge({}, isIssueIdEnabled, params))
    // scenarioCode and is_issue_key_enabled are pre-computed in the AngularJS controller
    // and passed via filterParams (same pattern as form-1118-ftc-edit, expense-trans-add-edit)
    params.scenarioCode = filterParams?.scenarioCode || filterParams?.scenario_code || params.scenarioCode || 'RAF';
    params.is_issue_key_enabled = filterParams?.is_issue_key_enabled || params.is_issue_key_enabled || 'N';

    // 3. Add clob_data and clob_settings
    params.clob_data = jsonObj;
    params.clob_settings = jsonSettings;

    // 4. Override specific keys
    params.jcd_key = jcdKey;
    params.tax_year = taxYear;
    params.scenario = scenario;
    params.filer_entity_key = filerEntityKey;

    // 5. Build checkLockedbj (matching: setLockObj(filer_combination_key))
    const checkLockObj = [{ combination_key: filerCombinationKey }];
    params.checkLockedbj = JSON.stringify(checkLockObj);

    // 6. Build FormData and POST
    const formData = new FormData();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        formData.append(key, params[key]);
      }
    });

    return this.http.post(url, formData).pipe(
      map((response: any) => response)
    );
  }

  /**
   * Private helper method to make load HTTP request
   */
  private _makeLoadRequest(baseUrl: string, params: any): Observable<any> {
    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlParams.set(key, params[key].toString());
      }
    });

    const url = `${baseUrl}/loadJsonObject?${urlParams.toString()}`;

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(url, '', { headers, withCredentials: true });
  }
}
